import React, { ReactNode, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { ValueOf } from 'global';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext, useWatch } from 'react-hook-form';
import { FieldValue } from 'react-hook-form/dist/types/fields';
import { BuilderCards } from './BuilderCards';
import { useSaleMode } from '../../../../../context';
import { useCalculations } from '../../../../../hooks';
import { deskingActions, deskingSelectors } from '../../../../../model';
import { FinanceFieldSelector } from '../../../../components/finance/FinanceFieldSelector';
import { FormSectionHeader, MenuSelector, NotificationAlert } from '../../../../components';
import { FinanceProgramFormValues, FormSectionProps, SaleStructureType } from '../../../../../types';

type Props = FormSectionProps & { notification: ReactNode | undefined; createProductSlot?: ReactNode };

export const MenuBuilder = ({
  sectionName,
  notification,
  createProductSlot,
  handleUpdateCollapse,
  isSupportBehaviorSetting,
  onClearStructureAndMenuOptions,
}: Props) => {
  const classes = useStyles();

  const { isCash, onChangeStructureType } = useSaleMode();

  const { control, watch } = useFormContext<FinanceProgramFormValues>();
  const [financeQuote, saleDetails, submission, excludeEquipmentSales] = useWatch({
    control,
    name: ['financeQuote', 'saleDetails', 'submission', 'displaySettings.excludeEquipmentSales'],
  });

  const dispatch = useDispatch();
  const isSectionOpen = useSelector((state: any) => deskingSelectors.isSectionOpen(state, sectionName));
  const isSectionCollapsed = useSelector((state: any) => deskingSelectors.isSectionCollapsed(state, sectionName));

  const { isCalculating } = useCalculations();

  useEffect(() => {
    const formRefetchFields: FieldValue<ValueOf<FinanceProgramFormValues, 'financeQuote' | 'saleDetails'>>[] = [
      'financeQuote.customTermTemporaryValue',
      'financeQuote.terms',
      'financeQuote.program',
      'financeQuote.commonRate',
      'saleDetails.financeAmount',
    ];

    const subscription = watch((value, { name }) => {
      if (!formRefetchFields.includes(name)) return;
      dispatch(deskingActions.updateRecalculate());
    });

    return subscription.unsubscribe;
  }, [dispatch, watch]);

  const isShowMenuBuilder = useMemo(() => {
    if (isCash && financeQuote.menu) return true;

    return !!financeQuote.terms?.length || !!financeQuote.customTermTemporaryValue;
  }, [isCash, financeQuote.menu, financeQuote.terms?.length, financeQuote.customTermTemporaryValue]);

  return (
    <Box className={clsx('section', classes.sectionContainer)}>
      <FormSectionHeader
        name="Menu Builder"
        sectionName={sectionName}
        isSectionOpen={isSectionOpen}
        isSectionCollapsed={isSectionCollapsed}
        handleUpdateCollapse={(section, nextValue) => {
          onChangeStructureType(SaleStructureType.AftermarketMenu);
          handleUpdateCollapse?.(section, nextValue);
        }}
        extraAction={
          <>
            {!isSectionCollapsed && (
              <Box className={classes.actionBtn}>
                <Button size="small" color="primary" variant="contained" onClick={onClearStructureAndMenuOptions}>
                  Reset
                </Button>
              </Box>
            )}
          </>
        }
      />

      {!isSectionCollapsed && (
        <Box className="row">
          <FinanceFieldSelector />

          <MenuSelector withAddProductsFeature={false} createProductSlot={createProductSlot} />
        </Box>
      )}

      {!isShowMenuBuilder && (
        <NotificationAlert message="Menu Template didn`t selected or missing Finance Inputs. Add Amount, Term and Buy Rate to render." />
      )}

      {notification && <NotificationAlert message={notification} />}

      <Box className={clsx('sectionFields')}>
        {isShowMenuBuilder && (
          <BuilderCards
            isSectionOpen={isSectionOpen}
            isCalculating={isCalculating}
            financeAmount={saleDetails.financeAmount}
            isFinanceExcluded={excludeEquipmentSales}
            isSupportBehaviorSetting={!!isSupportBehaviorSetting}
            isMAALessThenFinanceAmount={(submission.maxApprovalAmount ?? 0) < saleDetails.financeAmount}
          />
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  sectionContainer: {
    overflowX: 'auto',
  },
  sectionFieldsLoading: {
    height: '100%',
    minHeight: '10rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtn: {
    top: '50%',
    right: '10px',
    position: 'absolute',
    transform: 'translateY(-50%)',
  },
});
